export const ENROLLMENT_GOAL = 300;
export const ENROLLMENT_GOAL_DATE = new Date(2026, 9, 1);

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, " ")
    .replace(/\s+/g, " ");

const NON_ENROLLED_STATUSES = new Set([
  "inactive",
  "inquired",
  "left",
  "left school",
  "discontinued",
]);

const getStudentKey = (student, owner, index) => {
  const studentId = String(student?.studentId || "").trim();
  if (studentId) return `student:${studentId}`;

  const familyId = String(owner?.familyId || owner?.todoId || "").trim();
  const studentName = normalize(student?.studentName);
  return `family:${familyId}|student:${studentName || index}`;
};

const isNewStudentCard = (card) => {
  const description = normalize(card?.todoDescription);
  const list = normalize(card?.whichList);

  return (
    description !== "reassign" &&
    description !== "old student" &&
    list !== "orange" &&
    list !== "blue"
  );
};

const collectCardStudents = (cards, acceptedLists) => {
  const students = new Set();

  cards.forEach((card) => {
    if (!isNewStudentCard(card) || !acceptedLists.has(normalize(card?.whichList))) {
      return;
    }

    const studentList = Array.isArray(card?.studentList) ? card.studentList : [];
    studentList.forEach((student, index) => {
      students.add(getStudentKey(student, card, index));
    });
  });

  return students;
};

const removeKeys = (target, keysToRemove) => {
  keysToRemove.forEach((key) => target.delete(key));
  return target;
};

export const getEnrollmentGoalProgress = ({
  families = [],
  cards = [],
  studentsWithLessons,
} = {}) => {
  const activeStudentKeys = new Set();
  const summerBreakStudentKeys = new Set();

  families.forEach((family) => {
    const familyStatus = normalize(family?.familyStatus);
    const students = Array.isArray(family?.students) ? family.students : [];

    students.forEach((student, index) => {
      const studentStatus = normalize(student?.studentStatus);
      const studentKey = getStudentKey(student, family, index);

      if (studentStatus === "active") {
        activeStudentKeys.add(studentKey);
        return;
      }

      if (
        studentStatus === "summer break" ||
        (familyStatus === "summer break" && !NON_ENROLLED_STATUSES.has(studentStatus))
      ) {
        summerBreakStudentKeys.add(studentKey);
      }
    });
  });

  removeKeys(summerBreakStudentKeys, activeStudentKeys);

  const enrolledStudentKeys = new Set(activeStudentKeys);
  const studentsAlreadyRepresented = new Set([
    ...activeStudentKeys,
    ...summerBreakStudentKeys,
  ]);
  const onboardingStudentKeys = removeKeys(
    collectCardStudents(cards, new Set(["onboarding"])),
    studentsAlreadyRepresented
  );
  const introStudentKeys = removeKeys(
    collectCardStudents(cards, new Set(["intro"])),
    new Set([...studentsAlreadyRepresented, ...onboardingStudentKeys])
  );
  const schedulingStudentKeys = removeKeys(
    collectCardStudents(cards, new Set(["scheduling", "student"])),
    new Set([
      ...studentsAlreadyRepresented,
      ...onboardingStudentKeys,
      ...introStudentKeys,
    ])
  );

  const enrolledStudents = Number.isFinite(studentsWithLessons)
    ? Math.max(0, Math.round(studentsWithLessons))
    : enrolledStudentKeys.size;
  const projectedEnrollment =
    enrolledStudents +
    summerBreakStudentKeys.size +
    onboardingStudentKeys.size +
    introStudentKeys.size +
    schedulingStudentKeys.size;

  return {
    goal: ENROLLMENT_GOAL,
    activeStudents: activeStudentKeys.size,
    summerBreakStudents: summerBreakStudentKeys.size,
    enrolledStudents,
    onboardingStudents: onboardingStudentKeys.size,
    introStudents: introStudentKeys.size,
    schedulingStudents: schedulingStudentKeys.size,
    projectedEnrollment,
    remainingStudents: Math.max(ENROLLMENT_GOAL - enrolledStudents, 0),
    enrolledPercent: Math.min((enrolledStudents / ENROLLMENT_GOAL) * 100, 100),
    projectedPercent: Math.min((projectedEnrollment / ENROLLMENT_GOAL) * 100, 100),
  };
};

export const getDaysUntilEnrollmentGoal = (today = new Date()) => {
  const todayUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const goalUtc = Date.UTC(2026, 9, 1);

  return Math.max(Math.ceil((goalUtc - todayUtc) / 86400000), 0);
};
